import { slotStorageKey } from './accountSlot';

/**
 * Active-org id, mirrored to localStorage so the choice survives reloads and
 * a fresh axios request always knows which org to scope itself to (see the
 * `X-Org-Id` request interceptor in `lib/api.ts`). Framework-free like
 * `token.ts`, so the interceptor can read it without React context.
 */
// Per account slot (multi-account tabs must not fight over the active
// workspace); slot 0 keeps the original keys.
const STORAGE_KEY = slotStorageKey('aiai.activeOrgId');
const SLUG_STORAGE_KEY = slotStorageKey('aiai.activeOrgSlug');

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // localStorage unavailable (private mode, SSR, etc.) — fall back to
    // in-memory only for this session.
    return null;
  }
}

let activeOrgId: string | null = readStored(STORAGE_KEY);
// The active workspace slug drives the `/w/<slug>/…` request prefix. Stored
// alongside the id so the framework-free axios interceptor (lib/api.ts) can
// read it without React context — even on global pages that have no slug in
// the browser URL (e.g. account settings firing an org-scoped call).
let activeOrgSlug: string | null = readStored(SLUG_STORAGE_KEY);

function persist(key: string, value: string | null): void {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // Best-effort persistence only; the in-memory value still works.
  }
}

export function getActiveOrgId(): string | null {
  return activeOrgId;
}

export function setActiveOrgId(id: string | null): void {
  activeOrgId = id;
  persist(STORAGE_KEY, id);
}

export function getActiveOrgSlug(): string | null {
  return activeOrgSlug;
}

export function setActiveOrgSlug(slug: string | null): void {
  activeOrgSlug = slug;
  persist(SLUG_STORAGE_KEY, slug);
}
